import { ShieldCheck } from "lucide-react";
import { NavigationItem } from "../../types";
import { BuilderContext } from "../context";
import { hasRouteAccess } from "@/lib/auth/route-access";

/** The vault's landing page — also the route-access key in `ROLE_BASED_ROUTES`. */
export const VAULT_NAV_HREF = "/vault";

/**
 * Credential vault (issue #3278). A single top-level entry next to Work.
 *
 * WHO SEES IT: whoever `hasRouteAccess("/vault", role)` lets through — the SAME
 * rule `proxy.ts` enforces on the route (`ROLE_BASED_ROUTES["/vault"]`, today
 * WTC admin + WTC employee). Deriving it here instead of copying the role list
 * means the link and the route cannot drift: a role that would be bounced by
 * the middleware never sees the link. Client-portal users are excluded twice
 * (they render `ClientNavigation`, not this tree, and the route rule omits them).
 * Hiding the link is discoverability, not access control.
 *
 * HOW IT NAVIGATES: `fullPageLoad` — the renderers emit a plain `<a>`, never a
 * Next `<Link>`. The vault's protections (nonce CSP, COOP) are headers of a
 * document loaded AT a /vault URL (vault review H2); a client-side transition
 * would land vault UI in a Hub document. `VaultDocumentGuard` would catch it and
 * reload, but the link should not depend on the fallback.
 *
 * `order: 4.5` sits it between Work (4) and Insights (5) without renumbering the
 * sections whose `order` users may already have saved; a user reorder
 * re-normalizes to a dense 0-based sequence (`moveInList`).
 */
export function buildVaultSection(ctx: BuilderContext): NavigationItem | null {
  if (ctx.isClientUser || !hasRouteAccess(VAULT_NAV_HREF, ctx.userType)) return null;
  return {
    id: "vault",
    name: "Vault",
    href: VAULT_NAV_HREF,
    icon: ShieldCheck,
    visible: true,
    order: 4.5,
    fullPageLoad: true,
  };
}
